/**
 * COLLECTIONS — real membership, or the collection does not exist.
 *
 * "Browse by collection" used to be tiles that routed to a genre shelf under a
 * fancier name — "Detectives" opened the Crime genre. The owner noticed,
 * because a label that promises curation and delivers a query is a small lie
 * told forty times a day. This module is the honest replacement.
 *
 * `collections-data.json` is built by `scripts/fetch-collections.mjs`: each
 * collection is a hand-curated theme whose MEMBERSHIP was resolved from
 * Wikidata (CC0, keyed by IMDb id — the same shape as the awards data) and
 * then verified against Cinemeta at build time. Only collections with at
 * least eight titles the catalogue can actually serve are in the file, so
 * everything exported here is genuinely openable.
 *
 * The copy lives here rather than in the JSON because words are edited by
 * people and membership is edited by the script; mixing the two files means
 * every re-fetch is also a copy review.
 */
import type { MediaKind } from '$types';
import data from './collections-data.json';

export interface CollectionMember {
  id: string;
  kind: MediaKind;
  /** First release year, from Wikidata P577. Missing on a few. */
  year?: number;
}

export interface Collection {
  slug: string;
  /** The tile's big word. */
  label: string;
  /** The tile's quiet second line. */
  note: string;
  /** The one-sentence description at the top of the collection's page. */
  blurb: string;
  /** Which kind-scoped browse pages file this tile. */
  kinds: MediaKind[];
  members: CollectionMember[];
}

interface Copy {
  label: string;
  note: string;
  blurb: string;
}

/*
 * Copy for every collection the fetch script knows how to build. A slug here
 * with no members in the JSON simply does not render — which is exactly what
 * happens to a collection that fell under eight catalogue survivors.
 */
const COPY: Record<string, Copy> = {
  heists: {
    label: 'The big score',
    note: 'Heists and perfect plans',
    blurb: 'One last job, a good crew, a clean plan — and the thing that goes wrong.'
  },
  'neo-noir': {
    label: 'Neo-noir',
    note: 'Crime, shadows, neon',
    blurb: 'Film noir with modern nerves: night streets, bad bargains and worse luck.'
  },
  ghibli: {
    label: 'Studio Ghibli',
    note: 'The Ghibli films',
    blurb: 'Hand-drawn worlds from the studio that never hurried.'
  },
  a24: {
    label: 'A24',
    note: 'The A24 catalogue',
    blurb: 'The label that made small films loud.'
  },
  'mind-benders': {
    label: 'Mind-benders',
    note: 'Psychological thrillers',
    blurb: 'Films that quietly remove the floor you thought you were standing on.'
  },
  'time-travel': {
    label: 'Out of time',
    note: 'Time travel and paradoxes',
    blurb: 'Stories that run on clocks that refuse to behave.'
  },
  spycraft: {
    label: 'Spycraft',
    note: 'Spies and tradecraft',
    blurb: 'Cover identities, dead drops, and people who lie beautifully.'
  },
  'space-opera': {
    label: 'Space opera',
    note: 'Galaxies at full scale',
    blurb: 'Fleets, empires and the far side of the sky.'
  },
  whodunits: {
    label: 'Whodunits',
    note: 'Suspects and locked rooms',
    blurb: 'Everyone in the house is lying; one of them is worse.'
  },
  courtroom: {
    label: 'The verdict',
    note: 'Trials and testimony',
    blurb: 'Cases argued in rooms where every word is on the record.'
  },
  'coming-of-age': {
    label: 'Growing pains',
    note: 'Coming-of-age films',
    blurb: 'The year everything changed, told from inside it.'
  },
  sitcoms: {
    label: 'Half-hour comfort',
    note: 'Sitcoms worth rewatching',
    blurb: 'Rooms full of people you know better than your neighbours.'
  },
  mockumentaries: {
    label: 'Deadpan cameras',
    note: 'Mockumentary film and TV',
    blurb: 'Fiction with a straight face and a camera crew.'
  },
  zombies: {
    label: 'The dead walk',
    note: 'Zombie films',
    blurb: 'Outbreaks, safe houses, and the arithmetic of survivors.'
  },
  slashers: {
    label: 'Slashers',
    note: 'The knife-edge classics',
    blurb: 'The genre that taught audiences to check the back seat.'
  },
  'martial-arts': {
    label: 'Fists and forms',
    note: 'Martial arts cinema',
    blurb: 'Choreography as argument, from dojo floors to wire work.'
  },
  'road-movies': {
    label: 'The open road',
    note: 'Road movies',
    blurb: 'Stories that only move when the wheels do.'
  }
};

/** Display order — curated, not alphabetical. Missing slugs are skipped. */
const ORDER = [
  'heists',
  'neo-noir',
  'ghibli',
  'a24',
  'mind-benders',
  'time-travel',
  'spycraft',
  'space-opera',
  'whodunits',
  'courtroom',
  'coming-of-age',
  'sitcoms',
  'mockumentaries',
  'zombies',
  'slashers',
  'martial-arts',
  'road-movies'
];

const DATA = data as Record<string, { members: CollectionMember[] }>;

/**
 * Which kind-scoped pages a collection appears on: any kind with at least four
 * members. A film collection with one stray series should not turn up on the
 * Shows page promising something it barely has.
 */
function kindsOf(members: CollectionMember[]): MediaKind[] {
  const kinds: MediaKind[] = [];
  for (const kind of ['movie', 'series'] as const) {
    if (members.filter((m) => m.kind === kind).length >= 4) kinds.push(kind);
  }
  if (kinds.length === 0 && members.length > 0) kinds.push(members[0]!.kind);
  return kinds;
}

/** Every shippable collection, in display order. */
export const COLLECTIONS: Collection[] = ORDER.filter((slug) => COPY[slug] && DATA[slug]?.members?.length).map(
  (slug) => ({
    slug,
    ...COPY[slug]!,
    kinds: kindsOf(DATA[slug]!.members),
    members: DATA[slug]!.members
  })
);

/** One collection by slug, or null — the route can carry anything. */
export function collectionBySlug(slug: string): Collection | null {
  return COLLECTIONS.find((c) => c.slug === slug) ?? null;
}
